// src/theme/components.js (IMPROVED)
import { alpha } from '@mui/material/styles';
import { colorTokens } from './colors';

// Component style tokens - GLOBAL DESIGN SYSTEM
export const componentTokens = {
  // Core design tokens
  borderRadius: {
    small: 6,
    medium: 8,
    large: 12,
    button: 30, // Standard rounded button style
    card: 16,   // Standard card corners
    chip: 20,   // Standard chip style
    input: 30,  // Match input fields to button radius
  },
  
  // Button sizes - standardized across components
  buttonHeight: {
    small: 48.5,  // Match the Create Client button
    medium: 54,
    large: 60,
  },

  // Standard padding scales
  padding: {
    button: {
      small: '14px 20px',
      medium: '16px 28px',
      large: '18px 36px',
    },
    input: {
      small: '14px 20px',
      medium: '16px 24px',
      large: '18px 28px',
    },
    card: {
      small: '16px',
      medium: '24px',
      large: '32px',
    },
  },
  
  // Legacy spacing scales - kept for backward compatibility
  spacing: {
    compact: '0.5rem 1rem',
    standard: '0.625rem 1.5rem',
    comfortable: '0.75rem 2rem',
  },

  animations: {
    float: 'float 4s ease-in-out infinite',
    glow: 'glow 3s infinite',
    shine: 'shine 2s infinite',
    gradient: 'gradient 8s ease infinite',
  },

  transitions: {
    standard: 'all 0.3s ease',
    fast: 'all 0.2s ease',
    slow: 'all 0.5s ease',
  },

  tabs: {
    indicator: {
      height: 3,
      borderRadius: '2px 2px 0 0',
    },
    item: {
      fontWeight: {
        default: 500,
        selected: 600,
      },
      minHeight: 48,
      fontSize: '0.875rem',
      textTransform: 'none',
      padding: {
        horizontal: '1rem 1.5rem',
        vertical: '0.75rem 1rem',
      },
    },
    panel: {
      padding: '1.5rem 0',
    },
  },

  effects: {
    magicalButton: {
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
        transform: 'translateX(-100%)',
        transition: 'transform 0.6s',
      },
      '&:hover::after': {
        transform: 'translateX(100%)',
      },
    },
    
    // Button glow effect for primary "Create" buttons
    buttonGlow: {
      animation: 'glow 3s infinite ease-in-out',
      boxShadow: '0 0 15px rgba(242, 197, 38, 0.4), 0 0 10px rgba(242, 197, 38, 0.1)',
    },

    gradientBorder: (theme) => ({
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        padding: '2px',
        background: theme.aghrba.gradients.cardBorder,
        backgroundSize: '300% 300%',
        animation: 'gradient 5s ease infinite',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        zIndex: 0,
      },
    }),

    tabHover: (theme, mode) => ({
      backgroundColor: alpha(theme.palette.secondary.main, mode === 'dark' ? 0.08 : 0.05),
    }),

    tabSelected: (theme, mode) => ({
      backgroundColor: alpha(theme.palette.secondary.main, mode === 'dark' ? 0.12 : 0.08),
    }),
  },
};

// Shared utility functions for component styles
const createBaseStyles = (theme) => {
  const { palette: colors, aghrba } = theme;
  const mode = theme.palette.mode;
  
  return {
    baseButton: {
      borderRadius: 30, // Match MagicButton style
      position: 'relative',
      overflow: 'hidden',
      fontWeight: 600,
      fontFamily: 'Poppins, system-ui, -apple-system, sans-serif',
      // Removed magical button effects - was causing hover issues with pseudo-elements
      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', // Matches MagicButton
      // Enhanced hover/active effects removed to prevent conflicts
      // Transform is now handled by individual button components
      // Shine effect removed - was causing hover flickering issues
      // Individual button components can add their own effects if needed
      // Optional magical variants can be applied with classes
      '&.magical': {
        animation: `${componentTokens.animations.glow} 3s infinite`,
      },
    },
    
    baseInput: {
      borderRadius: componentTokens.borderRadius.button,
      backgroundColor: mode === 'dark' 
        ? colors.background.light
        : alpha(colors.background.light, 0.7),
      transition: componentTokens.transitions.standard,
      color: colors.text.primary,
    },
    
    focusStyles: (color) => ({
      boxShadow: `0 0 0 3px ${alpha(color, 0.2)}`,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: color,
        borderWidth: '2px',
      },
    }),
    
    hoverStyles: (color) => ({
      backgroundColor: alpha(color, 0.1),
      color: color,
    }),
  };
};

/**
 * Creates component style overrides based on theme
 * @param {Object} theme - Theme object with colors, effects, etc.
 * @returns {Object} Component style overrides
 */
export const createComponentOverrides = (theme) => {
  const { palette: colors, aghrba } = theme;
  const mode = theme.palette.mode;
  const baseStyles = createBaseStyles(theme);
  
  return {
    MuiBox: {
      styleOverrides: {
        root: {
          borderRadius: componentTokens.borderRadius.medium,
        },
      },
    },
    
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: aghrba.gradients.pageBackground,
          minHeight: '100vh',
        },
        // Add global animation keyframes
        '@keyframes float': {
          '0%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-10px)' },
          '100%': { transform: 'translateY(0px)' }
        },
        '@keyframes glow': {
          '0%': { boxShadow: `0 0 5px ${alpha(colors.secondary.main, 0.4)}, 0 0 10px ${alpha(colors.secondary.main, 0.1)}` },
          '50%': { boxShadow: `0 0 20px ${alpha(colors.secondary.main, 0.6)}, 0 0 15px ${alpha(colors.secondary.main, 0.3)}` },
          '100%': { boxShadow: `0 0 5px ${alpha(colors.secondary.main, 0.4)}, 0 0 10px ${alpha(colors.secondary.main, 0.1)}` }
        },
        '@keyframes shine': {
          '0%': { backgroundPosition: '-100% 0' },
          '100%': { backgroundPosition: '200% 0' }
        },
        '@keyframes gradient': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' }
        },
        // Global fix for SVG icons in buttons
        '.MuiButton-contained .MuiSvgIcon-root, .MuiButton-containedPrimary .MuiSvgIcon-root, .MuiButton-containedSecondary .MuiSvgIcon-root': {
          color: '#ffffff !important',
          filter: 'brightness(1.2)',
        },
        // Target the specific problematic class directly
        '.css-htjysk-MuiSvgIcon-root': {
          color: '#ffffff !important',
        },
        // Handle any other icon cases
        '.MuiIconButton-root .MuiSvgIcon-root': {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.85)',
        },
        // Global styles for create buttons with enhanced UX
        '.create-button': {
          textTransform: 'none',
          boxShadow: `0 2px 8px ${alpha(colors.primary.main, 0.25)}`,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(colors.primary.main, 0.35)}`,
          },
          '&:active': {
            transform: 'translateY(0)',
          }
        },
        '*': {
          // transition: componentTokens.transitions.standard,
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*': {
            animationDuration: '0.001ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.001ms !important',
          }
        }
      },
    },
    
    MuiButton: {
      styleOverrides: {
        root: {
          ...baseStyles.baseButton,
          padding: '10px 24px',
          textTransform: 'none',
          // Fix for all button icons in light mode - use !important to override any component styles
          '& .MuiSvgIcon-root': {
            color: 'inherit !important', // Make icon color match the button text color
          },
        },
        sizeSmall: {
          padding: componentTokens.padding.button.small,
          fontSize: '0.75rem',
          height: componentTokens.buttonHeight.small,
          minWidth: componentTokens.buttonHeight.small * 2.5, // Proportional width
          borderRadius: componentTokens.borderRadius.button,
        },
        sizeMedium: {
          padding: componentTokens.padding.button.medium,
          fontSize: '0.875rem',
          height: componentTokens.buttonHeight.medium,
          minWidth: componentTokens.buttonHeight.medium * 2.5, // Proportional width
          borderRadius: componentTokens.borderRadius.button,
        },
        sizeLarge: {
          padding: componentTokens.padding.button.large,
          fontSize: '1rem',
          height: componentTokens.buttonHeight.large,
          minWidth: componentTokens.buttonHeight.large * 2.5, // Proportional width
          borderRadius: componentTokens.borderRadius.button,
        },
        // Primary styling with gradient background - optimized for create buttons
        containedPrimary: {
          background: aghrba.gradients.primaryButton || `linear-gradient(45deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
          color: colors.primary.contrastText,
          boxShadow: colorTokens.shadows.buttonShadow(mode, colors.primary.main) || '0 4px 10px rgba(13, 46, 77, 0.3)',
          textTransform: 'none',
          fontWeight: 600,
          // Ensure icons in contained buttons are white in light mode with !important
          '& .MuiSvgIcon-root': {
            color: '#ffffff !important',
            filter: 'brightness(1.2)',
          },
          '&:hover': {
            boxShadow: mode === 'dark' 
              ? `0 6px 12px ${alpha(colors.primary.main, 0.4)}` 
              : `0 6px 12px ${alpha(colors.primary.main, 0.15)}`,
            background: `linear-gradient(45deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        // Secondary styling with gradient background - matches Create Client button
        containedSecondary: {
          background: aghrba.gradients.secondaryButton || `linear-gradient(45deg, ${colors.secondary.dark} 0%, ${colors.secondary.main} 100%)`,
          boxShadow: colorTokens.shadows.buttonShadow(mode, colors.secondary.main) || '0 4px 10px rgba(242, 197, 38, 0.3)',
          color: colors.secondary.contrastText,
          // Ensure icons in contained secondary buttons are also white in light mode with !important
          '& .MuiSvgIcon-root': {
            color: '#ffffff !important',
            filter: 'brightness(1.2)',
          },
          '&:hover': {
            boxShadow: mode === 'dark' 
              ? `0 6px 12px ${alpha(colors.secondary.main, 0.3)}` 
              : `0 6px 12px ${alpha(colors.secondary.main, 0.25)}`,
            background: `linear-gradient(45deg, ${colors.secondary.main} 0%, ${colors.secondary.light} 100%)`,
          },
        },
        // Outlined variants with animation effects
        outlined: {
          border: `2px solid ${colors.primary.main}`,
          color: colors.text.primary,
          // Ensure icons match button text color
          '& .MuiSvgIcon-root': {
            color: 'inherit',
            opacity: 0.9,
          },
          '&:hover': {
            ...baseStyles.hoverStyles(colors.primary.main),
            boxShadow: `0 4px 8px ${alpha(colors.primary.main, 0.1)}`,
            // Enhance icon visibility on hover
            '& .MuiSvgIcon-root': {
              opacity: 1,
            },
          },
        },  
        outlinedPrimary: {
          border: `2px solid ${colors.primary.main}`,
          color: colors.text.primary,
          // Ensure icons inherit text color and have good contrast
          '& .MuiSvgIcon-root': {
            color: colors.primary.main,
            opacity: 0.9,
          },
          '&:hover': {
            ...baseStyles.hoverStyles(colors.primary.main),
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 8px ${alpha(colors.primary.main, 0.1)}`,
            // Enhance icon visibility on hover
            '& .MuiSvgIcon-root': {
              opacity: 1,
            },
          },
        },
        outlinedSecondary: {
          border: `2px solid ${colors.secondary.main}`,
          color: colors.secondary.main,
          // Ensure icons match the secondary color
          '& .MuiSvgIcon-root': {
            color: colors.secondary.main,
            opacity: 0.9,
          },
          '&:hover': {
            ...baseStyles.hoverStyles(colors.secondary.main),
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 8px ${alpha(colors.secondary.main, 0.1)}`,
            // Enhance icon visibility on hover
            '& .MuiSvgIcon-root': {
              opacity: 1,
            },
          },
        },
        textSecondary: {
          color: colors.secondary.main,
          // Ensure icons match the secondary color
          '& .MuiSvgIcon-root': {
            color: colors.secondary.main,
            opacity: 0.9,
          },
          '&:hover': {
            ...baseStyles.hoverStyles(colors.secondary.main),
            transform: 'translateY(-2px)',
            // Enhance icon visibility on hover
            '& .MuiSvgIcon-root': {
              opacity: 1,
            },
          },
        },  
        text: {
          color: colors.text.primary,
          // Ensure icons match the text color
          '& .MuiSvgIcon-root': {
            color: 'inherit',
            opacity: 0.9,
          },
          '&:hover': {
            ...baseStyles.hoverStyles(colors.text.secondary),
            transform: 'translateY(-2px)',
            // Enhance icon visibility on hover
            '& .MuiSvgIcon-root': {
              opacity: 1,
            },
          },
        },
      },
    },
    
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: mode === 'dark' 
            ? alpha(theme.palette.common.white, 0.9) // Better visibility in dark mode
            : alpha(colors.text.primary, 0.85),     // Slightly darker in light mode for contrast
          transition: componentTokens.transitions.standard,
          filter: mode === 'light' ? 'contrast(1.1)' : 'none', // Enhance contrast in light mode
          '&:hover': {
            color: mode === 'dark' ? colors.secondary.main : colors.primary.main,
            filter: 'contrast(1.2)', // Enhanced contrast on hover
          },
          // Direct fix for the specific class mentioned by the user
          '&.css-htjysk-MuiSvgIcon-root': {
            color: '#ffffff !important', // Force white color for these icons
          },
          // Fix for all button icons in light mode - targeting the more specific class
          '.MuiButton-contained &, .MuiButton-containedPrimary &, .MuiButton-containedSecondary &': {
            color: '#ffffff !important',
            filter: 'brightness(1.2)',
          },
        },
        colorPrimary: {
          color: colors.primary.main,
          filter: mode === 'light' ? 'contrast(1.05) brightness(0.95)' : 'none', // Enhance visibility
          '&:hover': {
            color: colors.primary.dark, // Use dark for better visibility in light mode
            filter: 'contrast(1.1) brightness(1)',
          },
          // More direct targeting of primary colored icons in buttons
          '.MuiButton-containedPrimary &': {
            color: '#ffffff !important',
          },
          '.MuiButton-outlinedPrimary &, .MuiButton-textPrimary &': {
            color: 'inherit',
          },
        },
        colorSecondary: {
          color: colors.secondary.main,
          filter: mode === 'light' ? 'contrast(1.05) brightness(0.95)' : 'none', // Enhance visibility
          '&:hover': {
            color: colors.secondary.dark, // Use dark for better visibility in light mode
            filter: 'contrast(1.1) brightness(1)',
          },
          // More direct targeting of secondary colored icons in buttons
          '.MuiButton-containedSecondary &': {
            color: '#ffffff !important',
          },
        },
        colorAction: {
          color: mode === 'dark' 
            ? alpha(theme.palette.common.white, 0.9)
            : alpha(colors.text.primary, 0.9), // Darker for better visibility
          '&:hover': {
            color: mode === 'dark' ? colors.secondary.main : colors.primary.main,
          },
        },
        fontSizeSmall: {
          fontSize: '1rem',
          // Target specifically small icons in buttons
          '.MuiButton-root &': {
            color: 'inherit', // Ensure small icons inherit button color
          },
          '.MuiButton-contained &, .MuiButton-containedPrimary &, .MuiButton-containedSecondary &': {
            color: '#ffffff !important', // Force white for contained buttons
          },
        },
        fontSizeMedium: {
          fontSize: '1.5rem',
        },
        fontSizeLarge: {
          fontSize: '2rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: componentTokens.borderRadius.card,
          boxShadow: colorTokens.shadows.cardShadow(mode),
          overflow: 'hidden',
          transition: componentTokens.transitions.standard,
          backgroundColor: colors.background.paper,
          // Add subtle border in dark mode for better definition
          ...(mode === 'dark' && {
            border: `1px solid ${alpha(colors.background.light, 0.08)}`,
          }),
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: mode === 'dark'
              ? `0 12px 20px rgba(0, 0, 0, 0.35), 0 0 0 1px ${alpha(colors.secondary.main, 0.1)}`
              : '0 12px 20px rgba(0, 0, 0, 0.12)',
          },
          // Improve focus state for keyboard navigation
          '&:focus-within': {
            boxShadow: mode === 'dark'
              ? `0 0 0 2px ${alpha(colors.secondary.main, 0.4)}, ${colorTokens.shadows.cardShadow(mode)}`
              : `0 0 0 2px ${alpha(colors.primary.main, 0.3)}, ${colorTokens.shadows.cardShadow(mode)}`,
          },
        },
      },
    },
    
    // Input components use shared base styles
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          ...baseStyles.baseInput,
          '&:hover:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(colors.secondary.main, 0.5),
          },
          '&.Mui-focused:not(.Mui-error)': baseStyles.focusStyles(colors.secondary.main),
          '&.Mui-error': {
            boxShadow: `0 0 0 2px ${alpha(colors.error.main, 0.15)}`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${colors.error.main} !important`,
              borderWidth: '2px',
            },
            // Ensure error style has higher priority than focus
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${colors.error.main} !important`,
              borderWidth: '2px',
            },
          },
        },
        notchedOutline: {
          borderColor: mode === 'dark'
            ? alpha(colors.secondary.main, 0.5)
            : alpha(colors.secondary.main, 0.3),
          borderWidth: '1px', // Fixed border width for visibility
          transition: componentTokens.transitions.standard,
        },
        input: {
          padding: '12px 16px',
        },
      },
    },
    
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            ...baseStyles.baseInput,
            borderRadius: componentTokens.borderRadius.input,
            height: componentTokens.buttonHeight.small,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? colors.secondary.main : colors.primary.main,
            },
            '&.Mui-focused': baseStyles.focusStyles(mode === 'dark' ? colors.secondary.main : colors.primary.main),
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '& .MuiInputBase-input': {
            padding: componentTokens.padding.input.small,
            height: 'auto',
            '&::placeholder': {
              color: colors.text.disabled,
              opacity: 1,
            },
          },
          '& .MuiInputLabel-root': {
            color: mode === 'dark'
              ? alpha(theme.palette.common.white, 0.8)
              : colors.text.secondary,
            fontWeight: 500,
            // Only apply focus styles when not in error state
            '&.Mui-focused:not(.Mui-error)': {
              color: mode === 'dark' ? colors.secondary.main : colors.primary.main,
              textShadow: mode === 'dark' ? '0 0 1px rgba(255,255,255,0.2)' : 'none',
            },
            // Error state with !important to ensure higher priority
            '&.Mui-error': {
              color: `${colors.error.main} !important`,
              fontWeight: 600,
            },
            // Ensure error state overrides focus state when both are active
            '&.Mui-focused.Mui-error': {
              color: `${colors.error.main} !important`,
              fontWeight: 600,
            },
            // Only apply shrink styles when not in error state
            '&.MuiInputLabel-shrink:not(.Mui-error)': {
              color: mode === 'dark' ? colors.secondary.main : colors.primary.main,
              fontWeight: 600,
            },
          },
        },
      },
    },
    
    MuiInputBase: {
      styleOverrides: {
        root: {
          ...baseStyles.baseInput,
        },
        input: {
          padding: '12px 16px',
        },
      },
    },

    // Shared styling for hover states
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: componentTokens.tabs.item.minHeight,
          textTransform: componentTokens.tabs.item.textTransform,
          fontWeight: componentTokens.tabs.item.fontWeight.default,
          fontSize: componentTokens.tabs.item.fontSize,
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.8)
            : alpha(theme.palette.common.black, 0.7),
          transition: componentTokens.transitions.standard,
          '&:hover': {
            color: alpha(colors.secondary.main, 0.9),
            ...componentTokens.effects.tabHover(theme, mode),
          },
          '&.Mui-selected': {
            color: colors.secondary.main,
            fontWeight: componentTokens.tabs.item.fontWeight.selected,
            ...componentTokens.effects.tabSelected(theme, mode),
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
            marginBottom: 0,
            opacity: mode === 'dark' ? 0.9 : 0.7,
          },
          '&.Mui-selected .MuiSvgIcon-root': {
            color: colors.secondary.main,
            opacity: 1,
          },
        },
        labelIcon: {
          '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
            marginBottom: 0,
          },
        },
        // Horizontal tabs padding
        wrapped: {
          padding: componentTokens.tabs.item.padding.horizontal,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha(colors.background.light, mode === 'dark' ? 0.4 : 0.6)}`,
          position: 'relative',
        },
        indicator: {
          backgroundColor: colors.secondary.main,
          borderRadius: componentTokens.tabs.indicator.borderRadius,
          height: componentTokens.tabs.indicator.height,
          transition: componentTokens.transitions.standard,
          boxShadow: `0 0 8px ${alpha(colors.secondary.main, mode === 'dark' ? 0.8 : 0.5)}`,
        },
        flexContainer: {
          gap: theme.spacing(1),
        },
        scrollButtons: {
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.8)
            : alpha(theme.palette.common.black, 0.7),
          '&.Mui-disabled': {
            color: alpha(colors.text.disabled, 0.3),
          },
        },
        vertical: {
          '& .MuiTabs-indicator': {
            width: 3,
            right: 0,
            boxShadow: `0 0 8px ${alpha(colors.secondary.main, mode === 'dark' ? 0.8 : 0.5)}`,
          },
          '& .MuiTab-root': {
            padding: componentTokens.tabs.item.padding.vertical,
            justifyContent: 'flex-start',
            alignItems: 'center',
            '&.Mui-selected': {
              borderRight: `3px solid ${colors.secondary.main}`,
            },
          },
        },
      },
    },

    MuiTabPanel: {
      styleOverrides: {
        root: {
          padding: componentTokens.tabs.panel.padding,
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.95)
            : alpha(theme.palette.common.black, 0.9),
          '& .MuiTypography-root': {
            color: 'inherit'
          },
          '& p, & span, & div': {
            color: mode === 'dark'
              ? alpha(theme.palette.common.white, 0.9)
              : alpha(theme.palette.common.black, 0.87)
          },
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          fontWeight: 700,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
        colorPrimary: {
          // backgroundColor: alpha(colors.primary.main, 0.1),
          // color: colors.primary.main,
          border: `1px solid ${alpha(colors.primary.main, 0.3)}`,
        },
        colorSecondary: {
          // backgroundColor: alpha(colors.secondary.main, 0.1),
          // color: mode === 'dark' ? colors.secondary.main : colors.secondary.dark,
          border: `1px solid ${alpha(colors.secondary.main, 0.3)}`,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          backgroundColor: colors.background.light,
          overflow: 'hidden',
        },
        bar: {
          background: `linear-gradient(90deg, ${colors.secondary.light}, ${colors.secondary.dark})`,
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        h1: {
          fontFamily: '"Playfair Display", serif',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.95)
            : colors.text.primary,
        },
        h2: {
          fontFamily: '"Playfair Display", serif',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.95)
            : colors.text.primary,
        },
        h3: {
          fontFamily: '"Playfair Display", serif',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.95)
            : colors.text.primary,
        },
        h4: {
          fontFamily: '"Playfair Display", serif',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.95)
            : colors.text.primary,
        },
        h5: {
          fontFamily: '"Poppins", sans-serif',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.95)
            : colors.text.primary,
        },
        h6: {
          fontFamily: '"Poppins", sans-serif',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.95)
            : colors.text.primary,
        },
        body1: {
          fontFamily: '"Poppins", sans-serif',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.9)
            : colors.text.primary,
        },
        body2: {
          fontFamily: '"Poppins", sans-serif',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.8)
            : colors.text.secondary,
        },
        caption: {
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.7)
            : colors.text.secondary,
        },
      },
    },

    // CardContent - enhance visibility of card content
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3),
          // Enhanced text colors in dark mode
          '& .MuiTypography-root': {
            color: mode === 'dark'
              ? alpha(theme.palette.common.white, 0.95)
              : undefined,
          },
          // Add padding between elements
          '& > *:not(:last-child)': {
            marginBottom: theme.spacing(2),
          },
          // Enhance borders for dividers in card
          '& .MuiDivider-root': {
            borderColor: mode === 'dark'
              ? alpha(colors.background.light, 0.2)
              : alpha(colors.background.light, 0.6),
            margin: theme.spacing(2, 0),
          },
        },
      },
    },

    // Add missing components from source
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          color: colors.text.primary,
          boxShadow: mode === 'dark' 
            ? '0 4px 16px rgba(0, 0, 0, 0.2)' 
            : '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.background.light,
          opacity: mode === 'dark' ? 0.5 : 1,
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          transition: componentTokens.transitions.standard,
          // Improve icon visibility in light mode
          '& .MuiSvgIcon-root': {
            color: mode === 'dark' 
              ? alpha(theme.palette.common.white, 0.9)
              : colors.text.primary,
            opacity: 0.85,
            transition: 'opacity 0.2s ease, color 0.2s ease, transform 0.2s ease',
          },
          '&:hover': {
            ...baseStyles.hoverStyles(mode === 'dark' ? colors.secondary.main : colors.primary.main),
            transform: 'translateY(-2px)',
            // Enhanced icon visibility on hover
            '& .MuiSvgIcon-root': {
              color: mode === 'dark' ? colors.secondary.main : colors.primary.main,
              opacity: 1,
              transform: 'scale(1.05)',
            },
          },
          // Enhanced styling for small button variation used in cards
          '&.MuiIconButton-sizeSmall': {
            padding: 8,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 8px ${alpha(colors.primary.main, 0.1)}`,
            },
          },
          // Primary color variation
          '&.MuiIconButton-colorPrimary': {
            '& .MuiSvgIcon-root': {
              color: colors.primary.main,
              opacity: 0.9,
            },
            '&:hover .MuiSvgIcon-root': {
              color: colors.primary.dark,
              opacity: 1,
            },
          },
          // Secondary color variation
          '&.MuiIconButton-colorSecondary': {
            '& .MuiSvgIcon-root': {
              color: colors.secondary.main,
              opacity: 0.9,
            },
            '&:hover .MuiSvgIcon-root': {
              color: colors.secondary.dark,
              opacity: 1,
            },
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'dark' ? colors.background.light : colors.primary.dark,
          color: colors.primary.contrastText,
          fontSize: '0.875rem',
          padding: '8px 12px',
          borderRadius: 8,
          boxShadow: mode === 'dark' 
            ? '0 4px 12px rgba(0, 0, 0, 0.25)' 
            : '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: componentTokens.borderRadius.medium,
          boxShadow: mode === 'dark'
            ? `0 4px 12px rgba(0, 0, 0, 0.35), 0 0 0 1px ${alpha(colors.background.light, 0.1)}`
            : '0 4px 12px rgba(0, 0, 0, 0.15)',
          backgroundColor: colors.background.paper,
          border: mode === 'dark'
            ? `1px solid ${alpha(colors.background.light, 0.1)}`
            : `1px solid ${alpha(colors.background.light, 0.5)}`,
          // Add subtle outline effect in dark mode for better visibility
          ...(mode === 'dark' && {
            outline: `1px solid ${alpha(colors.secondary.main, 0.1)}`,
            outlineOffset: -1,
          }),
          // Enhanced styling with arrow
          marginTop: theme.spacing(1.5),
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
          // Arrow effect
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
            // Apply appropriate border to arrow in dark mode
            ...(mode === 'dark' && {
              borderTop: `1px solid ${alpha(colors.background.light, 0.1)}`,
              borderLeft: `1px solid ${alpha(colors.background.light, 0.1)}`,
            }),
          },
        },
        list: {
          // Add padding to make the menu more visually appealing
          padding: theme.spacing(1, 0),
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: componentTokens.borderRadius.medium,
          boxShadow: mode === 'dark'
            ? `0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px ${alpha(colors.background.light, 0.1)}`
            : '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 16px',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.9)
            : colors.text.primary,
          borderRadius: 6, // Slightly larger radius for better visual appeal
          margin: theme.spacing(0, 0.5), // Add margin for better visual separation
          transition: componentTokens.transitions.standard,
          '&:hover': {
            ...baseStyles.hoverStyles(colors.secondary.main),
            backgroundColor: mode === 'dark'
              ? alpha(colors.secondary.main, 0.15)
              : alpha(colors.secondary.main, 0.08),
            transform: 'translateY(-1px)', // Subtle lift effect on hover
          },
          '&.Mui-selected': {
            backgroundColor: mode === 'dark'
              ? alpha(colors.secondary.main, 0.2)
              : alpha(colors.secondary.main, 0.1),
            fontWeight: 600,
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? alpha(colors.secondary.main, 0.25)
                : alpha(colors.secondary.main, 0.15),
              transform: 'translateY(-1px)', // Maintain consistent hover effect
            },
          },
          '&.Mui-disabled': {
            color: mode === 'dark'
              ? alpha(theme.palette.common.white, 0.3)
              : alpha(theme.palette.common.black, 0.3),
          },
          // Enhance ListItemIcon colors in dark mode
          '& .MuiListItemIcon-root': {
            color: mode === 'dark'
              ? alpha(theme.palette.common.white, 0.9)
              : colors.text.primary,
            minWidth: 36, // Reduce min width for better visual balance
          },
          // Enhance ListItemText colors in dark mode
          '& .MuiListItemText-primary': {
            color: 'inherit',
            fontWeight: 'inherit',
          },
          '& .MuiListItemText-secondary': {
            color: mode === 'dark'
              ? alpha(theme.palette.common.white, 0.6)
              : colors.text.secondary,
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: componentTokens.borderRadius.card,
          backgroundColor: colors.background.paper,
          backgroundImage: 'none',
          // Add border for better definition in dark mode
          border: mode === 'dark'
            ? `1px solid ${alpha(colors.background.light, 0.1)}`
            : 'none',
          // Add box shadow for better visibility
          boxShadow: mode === 'dark'
            ? `0 10px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px ${alpha(colors.background.light, 0.05)}`
            : '0 10px 24px rgba(0, 0, 0, 0.2)',
          // Add subtle outline for secondary color accent
          ...(mode === 'dark' && {
            outline: `1px solid ${alpha(colors.secondary.main, 0.1)}`,
            outlineOffset: -1,
          }),
          // Improve scrollbar in dialog
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'dark'
              ? alpha(colors.background.light, 0.2)
              : alpha(colors.background.light, 0.5),
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: mode === 'dark'
              ? alpha(colors.background.default, 0.2)
              : alpha(colors.background.light, 0.1),
          },
          // Handle transition better
          transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
        },
        // Add style for backdrop
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: mode === 'dark'
              ? alpha(colors.background.default, 0.7)
              : alpha(colors.background.default, 0.5),
            backdropFilter: 'blur(4px)',
          },
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Playfair Display", serif',
          fontSize: '1.5rem',
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.95)
            : colors.text.primary,
          padding: theme.spacing(3, 3, 1, 3),
          fontWeight: 600,
          // Add border for section separation
          borderBottom: mode === 'dark'
            ? `1px solid ${alpha(colors.background.light, 0.1)}`
            : `1px solid ${alpha(colors.background.light, 0.2)}`,
          // Add background for better visibility
          backgroundColor: mode === 'dark'
            ? alpha(colors.background.paper, 0.7)
            : alpha(colors.background.paper, 0.95),
          backdropFilter: 'blur(8px)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3),
          // Ensure proper text color in dark mode
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.9)
            : colors.text.primary,
          // Add padding at the bottom
          '&:last-child': {
            paddingBottom: theme.spacing(3),
          },
          // Enhance Typography inside dialog
          '& .MuiTypography-root': {
            marginBottom: theme.spacing(2),
            color: mode === 'dark'
              ? alpha(theme.palette.common.white, 0.9)
              : undefined,
          },
          // Enhance FormControl inside dialog
          '& .MuiFormControl-root': {
            marginBottom: theme.spacing(2.5),
          },
        },
        dividers: {
          borderColor: mode === 'dark'
            ? alpha(colors.background.light, 0.1)
            : alpha(colors.background.light, 0.2),
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2, 3, 3),
          // Add border for better visibility
          borderTop: mode === 'dark'
            ? `1px solid ${alpha(colors.background.light, 0.1)}`
            : `1px solid ${alpha(colors.background.light, 0.2)}`,
          // Add background for sticky footer
          backgroundColor: mode === 'dark'
            ? alpha(colors.background.paper, 0.7)
            : alpha(colors.background.paper, 0.95),
          backdropFilter: 'blur(8px)',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          // Add spacing between buttons
          '& .MuiButton-root': {
            marginLeft: theme.spacing(1),
          },
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: colors.secondary.main,
        },
      },
    },

    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          color: colors.text.primary,
          boxShadow: colorTokens.shadows.cardShadow(mode),
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: colors.secondary.main,
            '& + .MuiSwitch-track': {
              backgroundColor: colors.secondary.main,
              opacity: 0.5,
            },
          },
        },
        track: {
          backgroundColor: mode === 'dark' ? colors.background.light : colors.text.disabled,
          opacity: 0.5,
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          '&.Mui-checked': {
            color: colors.secondary.main,
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          '&.Mui-checked': {
            color: colors.secondary.main,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: componentTokens.borderRadius.button,
          padding: '12px 16px',
          // backgroundColor: mode === 'dark' 
          //   ? colors.background.light
          //   : alpha(colors.background.light, 0.7),
          color: colors.text.primary,
          '&:focus': {
            backgroundColor: mode === 'dark' 
              ? colors.background.light
              : alpha(colors.background.light, 0.7),
          },
        },
      },
    },
    
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: theme.spacing(2),
          '& .MuiInputLabel-root': {
            color: alpha(colors.secondary.main, 1),
            transition: componentTokens.transitions.standard,
            '&.Mui-focused:not(.Mui-error)': {
              color: colors.secondary.main,
              fontWeight: 500,
              letterSpacing: '0.01em',
            },
            '&.Mui-error': {
              color: `${colors.error.main} !important`,
              fontWeight: 500,
            },
          },
          '& .MuiFormHelperText-root': {
            marginLeft: theme.spacing(1.5),
            marginTop: 4,
            fontSize: '0.75rem',
            color: mode === 'dark'
              ? alpha(theme.palette.common.white, 0.7)
              : colors.text.secondary,
            fontWeight: mode === 'dark' ? 500 : 400,
            '&.Mui-error': {
              color: mode === 'dark'
                ? alpha(colors.error.main, 0.9)
                : colors.error.main,
              fontWeight: 500,
            },
          },
        },
      },
    },
    
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.85)
            : colors.text.secondary,
          fontWeight: mode === 'dark' ? 500 : 400,
          // Only apply focus styles when not in error state
          '&.Mui-focused:not(.Mui-error)': {
            color: colors.secondary.main,
            fontWeight: 600,
          },
          // Error state with !important to ensure higher priority
          '&.Mui-error': {
            color: `${mode === 'dark'
              ? alpha(colors.error.main, 0.9)
              : colors.error.main} !important`,
            fontWeight: 600,
          },
          // Ensure error state overrides focus state when both are active
          '&.Mui-focused.Mui-error': {
            color: `${mode === 'dark'
              ? alpha(colors.error.main, 0.9)
              : colors.error.main} !important`,
            fontWeight: 600,
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.7)
            : colors.text.disabled,
          fontSize: '0.75rem',
          fontWeight: mode === 'dark' ? 500 : 400,
          lineHeight: 1.4,
          letterSpacing: '0.01em',
          '&.Mui-error': {
            color: `${mode === 'dark'
              ? alpha(colors.error.main, 0.9)
              : colors.error.main} !important`,
            fontWeight: 500,
          },
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' 
            ? alpha(colors.background.default, 0.8) 
            : alpha(colors.background.default, 0.6),
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          backgroundImage: 'none',
          borderRadius: componentTokens.borderRadius.medium,
          // Add subtle border in dark mode for better definition
          ...(mode === 'dark' && {
            border: `1px solid ${alpha(colors.background.light, 0.08)}`,
          }),
          '&:focus-within': {
            boxShadow: mode === 'dark'
              ? `0 0 0 2px ${alpha(colors.secondary.main, 0.25)}`
              : `0 0 0 2px ${alpha(colors.primary.main, 0.15)}`,
          },
        },
        rounded: {
          borderRadius: componentTokens.borderRadius.card,
        },
        elevation1: {
          boxShadow: mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)'
            : '0 2px 8px rgba(0, 0, 0, 0.06)',
        },
        elevation4: {
          boxShadow: mode === 'dark'
            ? '0 8px 16px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.07)'
            : '0 8px 16px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    MuiList: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1, 0),
          '& .MuiDivider-root': {
            margin: theme.spacing(0.5, 0),
          },
        },
        padding: {
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          transition: componentTokens.transitions.standard,
          borderRadius: 4,
          margin: theme.spacing(0, 0.5),
          '&:hover': {
            ...baseStyles.hoverStyles(colors.secondary.main),
            backgroundColor: mode === 'dark'
              ? alpha(colors.secondary.main, 0.15)
              : alpha(colors.secondary.main, 0.08),
          },
          // Add focus state for keyboard navigation
          '&:focus-visible': {
            outline: mode === 'dark'
              ? `2px solid ${alpha(colors.secondary.main, 0.5)}`
              : `2px solid ${alpha(colors.primary.main, 0.3)}`,
            outlineOffset: -1,
          },
        },
        button: {
          '&:hover': {
            backgroundColor: mode === 'dark'
              ? alpha(colors.secondary.main, 0.15)
              : alpha(colors.secondary.main, 0.08),
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.9)
            : colors.text.primary,
          minWidth: 36,
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.9)
            : colors.text.primary,
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 500,
        },
        secondary: {
          color: mode === 'dark'
            ? alpha(theme.palette.common.white, 0.6)
            : colors.text.secondary,
          fontSize: '0.75rem',
          lineHeight: 1.5,
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          boxShadow: 'none',
          border: `1px solid ${colors.background.light}`,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&:hover': baseStyles.hoverStyles(colors.secondary.main),
          '&.Mui-expanded': {
            borderBottom: `1px solid ${colors.background.light}`,
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: colors.background.light,
        },
        head: {
          backgroundColor: alpha(colors.background.light, 0.5),
          color: colors.text.primary,
          fontWeight: 600,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': baseStyles.hoverStyles(colors.secondary.main),
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },

    MuiStep: {
      styleOverrides: {
        root: {
          '& .MuiStepIcon-root': {
            color: colors.background.light,
            '&.Mui-active': {
              color: colors.secondary.main,
            },
            '&.Mui-completed': {
              color: colors.secondary.main,
            },
          },
        },
      },
    },

    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: colors.text.secondary,
          '&.Mui-active': {
            color: colors.text.primary,
            fontWeight: 600,
          },
          '&.Mui-completed': {
            color: colors.text.primary,
          },
        },
      },
    },

    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: colors.text.disabled,
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.secondary.main,
          transition: componentTokens.transitions.standard,
          '&:hover': {
            color: colors.secondary.light,
          },
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: colors.error.main,
          color: colors.error.contrastText,
          fontWeight: 600,
        },
      },
    },

    MuiSlider: {
      styleOverrides: {
        root: {
          color: colors.secondary.main,
        },
        thumb: {
          backgroundColor: colors.secondary.main,
          '&:hover': {
            boxShadow: `0 0 0 8px ${alpha(colors.secondary.main, 0.16)}`,
          },
        },
        track: {
          backgroundColor: colors.secondary.main,
          border: 'none',
        },
        rail: {
          backgroundColor: colors.background.light,
          opacity: 1,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          borderColor: mode === 'dark'
            ? alpha(colors.background.light, 0.1)
            : colors.background.light,
          // Add left border for better visibility in dark mode
          borderLeft: mode === 'dark'
            ? `1px solid ${alpha(colors.secondary.main, 0.1)}`
            : `1px solid ${alpha(colors.background.light, 0.6)}`,
          // Add box shadow for better definition
          boxShadow: mode === 'dark'
            ? '-8px 0 24px rgba(0, 0, 0, 0.3)'
            : '-8px 0 24px rgba(0, 0, 0, 0.1)',
          // Improve scrollbars in dark mode
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'dark'
              ? alpha(colors.background.light, 0.2)
              : alpha(colors.background.light, 0.5),
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: mode === 'dark'
              ? alpha(colors.background.default, 0.2)
              : alpha(colors.background.light, 0.1),
          },
        },
        // Add border to drawer header
        root: {
          '& .MuiDrawer-paperAnchorRight': {
            width: {
              xs: '100%',
              sm: 440,
              md: 480,
            },
            maxWidth: '100%',
          },
        },
      },
    },

    MuiSpeedDial: {
      styleOverrides: {
        fab: {
          background: aghrba.gradients.secondaryButton,
          '&:hover': {
            background: `linear-gradient(45deg, ${colors.secondary.main} 0%, ${colors.secondary.light} 100%)`,
          },
        },
      },
    },

    MuiSpeedDialAction: {
      styleOverrides: {
        fab: {
          backgroundColor: colors.background.paper,
          '&:hover': baseStyles.hoverStyles(colors.secondary.main),
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: mode === 'dark' 
            ? alpha(colors.success.main, 0.1) 
            : alpha(colors.success.main, 0.05),
          color: colors.success.main,
        },
        standardError: {
          backgroundColor: mode === 'dark' 
            ? alpha(colors.error.main, 0.1) 
            : alpha(colors.error.main, 0.05),
          color: colors.error.main,
        },
        standardWarning: {
          backgroundColor: mode === 'dark' 
            ? alpha(colors.warning.main, 0.1) 
            : alpha(colors.warning.main, 0.05),
          color: colors.warning.main,
        },
        standardInfo: {
          backgroundColor: mode === 'dark' 
            ? alpha(colors.info.main, 0.1) 
            : alpha(colors.info.main, 0.05),
          color: colors.info.main,
        },
      },
    },
  };
};